import { MagnifyingGlassSVGIcon } from '@/shared/assets'
import { SortHeader, StaticHeader } from '@/shared/components'
import { GreyRubleIconInline } from '@/shared/ui'
import { AnalyticsTableRowTypeSites } from '@/features/analytics/types'
import { type UseSearch, type UseSortedData } from '@/features/analytics/hooks'

import { DropdownMenuDemoSites } from '../DropdownMenuSites'

import {
    TABLE_COLUMNS_LAYOUT,
    type TableColumnOptionalKey,
    isColumnOptional,
    type TableReactNodeRecord,
} from './tableColumnsLayout'
import { Site } from '@/true-fsd/data/advertiser-account'

export type TableHeadRowProps = {
    skipColumns: Record<TableColumnOptionalKey, boolean>

    groupId: number
    activeSite: Site
} & Pick<
    UseSearch<AnalyticsTableRowTypeSites>,
    'setSearchQuery' | 'searchQuery'
> &
    Pick<UseSortedData<AnalyticsTableRowTypeSites>, 'sortConfig' | 'handleSort'>

export const TableHeadRow = ({
    skipColumns,

    groupId,
    activeSite,
    searchQuery,
    setSearchQuery,
    handleSort,
    sortConfig,
}: TableHeadRowProps) => {
    const tableHead: TableReactNodeRecord = {
        marking: <StaticHeader title="Маркировка" alignment="right" />,
        select: (
            <div className="flex h-full w-full items-center justify-center">
                <DropdownMenuDemoSites
                    groupId={groupId}
                    siteId={activeSite.site_id}
                />
            </div>
        ),
        name: (
            <div className="search-header w-full !gap-0">
                <div className="search-input-wrapper">
                    <MagnifyingGlassSVGIcon className="search-header__icon" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Название"
                        className="search-input"
                    />
                </div>
                <SortHeader<AnalyticsTableRowTypeSites>
                    title=""
                    field="name"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </div>
        ),
        status: <StaticHeader title="Статус" alignment="right" />,
        link: <StaticHeader title="Ссылка" alignment="right" />,
        creationDate: (
            <SortHeader<AnalyticsTableRowTypeSites>
                title="Дата"
                field="creationDate"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        creative: <StaticHeader title="Креатив" alignment="right" />,
        publishedAt: (
            <SortHeader<AnalyticsTableRowTypeSites>
                title="Дата выхода"
                field="publishedAt"
                sortConfig={sortConfig}
                onSort={handleSort}
                className="sort-header__flex-end"
                classNameSpan="sort-header__span__max-width"
            />
        ),
        publishingDeletionDate: (
            <SortHeader<AnalyticsTableRowTypeSites>
                title="Дата Удаления"
                field="publishingDeletionDate"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        subscribers: (
            <SortHeader<AnalyticsTableRowTypeSites>
                title="Кол-во пдп"
                field="subscribers"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        averageReach: (
            <SortHeader<AnalyticsTableRowTypeSites>
                title="Средний охват постов"
                field="averageReach"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        err: (
            <SortHeader<AnalyticsTableRowTypeSites>
                title="% ERR"
                field="err"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        CPMForcasted: (
            <SortHeader<AnalyticsTableRowTypeSites>
                title={
                    <>
                        Прогноз CPM <GreyRubleIconInline />
                    </>
                }
                field="CPMForcasted"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        reach: (
            <SortHeader<AnalyticsTableRowTypeSites>
                title="Охват поста"
                field="reach"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),

        CPM: (
            <SortHeader<AnalyticsTableRowTypeSites>
                title={
                    <>
                        CPM <GreyRubleIconInline />
                    </>
                }
                field="CPM"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),

        budget: (
            <SortHeader<AnalyticsTableRowTypeSites>
                title={
                    <>
                        Бюджет <GreyRubleIconInline />
                    </>
                }
                field="budget"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        CPU: (
            <SortHeader<AnalyticsTableRowTypeSites>
                title="CPU"
                field="CPU"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        CInPercent: (
            <SortHeader<AnalyticsTableRowTypeSites>
                title="C%"
                field="CInPercent"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        CPL: (
            <SortHeader<AnalyticsTableRowTypeSites>
                title="CPL"
                field="CPL"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        CTRInPercent: (
            <SortHeader<AnalyticsTableRowTypeSites>
                title="CTR%"
                field="CTRInPercent"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        leads: (
            <SortHeader<AnalyticsTableRowTypeSites>
                title="Leads"
                field="leads"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        users: (
            <SortHeader<AnalyticsTableRowTypeSites>
                title="Users"
                field="users"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
    }

    return (
        <tr>
            {TABLE_COLUMNS_LAYOUT.filter(
                (column) =>
                    !(isColumnOptional(column.key) && skipColumns[column.key])
            ).map(({ key, style, className }) => (
                <th key={key} style={style} className={className}>
                    {tableHead[key]}
                </th>
            ))}
        </tr>
    )
}
